export function timeToMinutes(time: string): number {
    const parts = time.split(':');
    return parseInt(parts[0]) * 60 + parseInt(parts[1]);
}

export const parseDate = (dateStr: string): Date | null => {
    const dateParts = dateStr.split('-');
    if (dateParts.length !== 3) {
        return null;
    }
    if (parseInt(dateParts[0]) < 2000) {
        // We assume that the date is jalali
        return dayConfig
            .gregory(dateStr, {
                format: 'YYYY-MM-DD',
                jalali: true,
            })
            .toDate();
    } else {
        return dayConfig
            .gregory(dateStr, { format: 'YYYY-MM-DD', utc: true })
            .toDate();
    }
};

export const formatDuration = (milliseconds: number): string => {
    const hours = Math.floor(milliseconds / 3600 / 1000);
    const minutes = Math.floor((Math.floor(milliseconds / 1000) % 3600) / 60);

    const parts = [];
    if (hours > 0) {
        parts.push(`${hours} ` + `ساعت`);
    }
    if (minutes > 0) {
        parts.push(`${minutes} ` + `دقیقه`);
    }

    return `${parts.join(' ' + 'و')} `;
};

export const formatTripDuration = (
    milliseconds: number,
    fullFormat?: boolean,
    hourOnly?: boolean
): string => {
    const hours = Math[hourOnly ? 'round' : 'floor'](
        milliseconds / 3600 / 1000
    );
    const minutes = Math.floor((Math.floor(milliseconds / 1000) % 3600) / 60);

    const parts = [];
    if (hours > 0) {
        parts.push(`${hours} ${fullFormat ? 'ساعت' : 'س'}`);
    }
    if (minutes > 0 && !hourOnly) {
        parts.push(`${minutes} ${fullFormat ? 'دقیقه' : 'د'}`);
    }

    return parts.join(' ');
};

export const secondsToDurationTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((Math.floor(seconds) % 3600) / 60);
    return toTwoDigitTime(`${hours}:${minutes}`);
};

export const secondsToDurationTimeSeparated = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((Math.floor(seconds) % 3600) / 60);
    return {
        hours,
        minutes,
    };
};

export const toTwoDigitTime = (time: string): string =>
    time
        .split(':')
        .map(s => s.padStart(2, '0'))
        .join(':');

export const formatSeparator = (
    price: number | string | null,
    separator = ','
): string => {
    if (price == null) {
        return '';
    }
    return (
        price
            .toString()
            .replace(/-/g, '')
            .split('')
            .reverse()
            .reduce(
                (acc, num, i) => num + (i && !(i % 3) ? separator : '') + acc,
                ''
            ) + (Number(price) < 0 ? '-' : '')
    );
};

/**
 * Formats the input price.
 *
 * Be aware of parameter `config.includeUnit`. If the unit is a Kilo-Unit then
 * the unit is hidden by default, else the unit is shown by default.
 * @param price
 * @param config
 */
export const formatMoney = (
    price: number | string | undefined | null,
    config?: {
        includeUnit?: boolean;
        unit?: 'Toman' | 'KToman';
        free?: boolean;
        separator?: string;
        fallback?: string;
        unitClassName?: string;
        sign?: '+' | '-';
        preserveSign?: boolean;
        expression?: 'round' | 'floor' | 'ceil';
    }
) => {
    const {
        includeUnit = undefined,
        unit = 'Toman',
        free = undefined,
        separator = ',',
        fallback = '-',
        unitClassName = '',
        sign = '',
        expression = 'round',
    } = config || {};
    if (
        price === null ||
        price === undefined ||
        price === '' ||
        isNaN(+price)
    ) {
        return fallback;
    }
    if (price === 0 && free !== false) {
        return free ? 'رایگان' : '0';
    }
    let actualPrice = config?.preserveSign ? +price : Math.abs(+price);
    switch (unit) {
        case 'Toman':
            actualPrice /= 10;
            break;
        case 'KToman':
            actualPrice /= 10000;
            break;
        default:
            throw new Error('Unit is not supported');
    }
    switch (expression) {
        case 'round':
            actualPrice = Math.round(actualPrice);
            break;
        case 'floor':
            actualPrice = Math.floor(actualPrice);
            break;
        case 'ceil':
            actualPrice = Math.ceil(actualPrice);
            break;
        default:
            throw new Error('Expression is not supported');
    }
    const formattedPrice = `${formatSeparator(actualPrice, separator)}${
        sign ?? ''
    }`;
    const formatWithClass = (unit: string) =>
        `${formattedPrice}<span class="${unitClassName}"> ${unit} </span>`;
    const toman = 'تومانء';
    switch (unit) {
        case 'Toman':
            if (includeUnit !== false) {
                if (unitClassName) {
                    return formatWithClass(toman);
                }
                return `${formattedPrice} ${toman}`;
            } else {
                return formattedPrice.toString();
            }
        case 'KToman':
            if (includeUnit === true) {
                if (unitClassName) {
                    return formatWithClass(`هزار ${toman}`);
                }
                return `${formattedPrice}هزار ${toman}`;
            } else {
                return formattedPrice.toString();
            }
    }
};

export type FormatMoney = typeof formatMoney;

export const formatCardNumber = (card: number | string): string => {
    if (card == null) {
        return '';
    }
    return card
        .toString()
        .split('')
        .reverse()
        .reduce(
            (acc, num, i) =>
                card === '-' ? acc : num + (i && !(i % 4) ? '-' : '') + acc,
            ''
        );
};

export const formatISO = (date: Date): string =>
    `${date.getFullYear()}-${(date.getMonth() + 1)
        .toString()
        .padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}T${date
        .getHours()
        .toString()
        .padStart(2, '0')}:${date
        .getMinutes()
        .toString()
        .padStart(2, '0')}:${date
        .getSeconds()
        .toString()
        .padStart(2, '0')}.${date
        .getMilliseconds()
        .toString()
        .padStart(3, '0')}Z`;

export const getTime = (dateTime: string): string => dateTime.substring(11, 16);

export const validateNationalCode = (
    rawInput: string | number | null
): boolean => {
    const input = rawInput?.toString() ?? '';
    if (
        !/^\d{10}$/.test(input) ||
        input === '0000000000' ||
        input === '1111111111' ||
        input === '2222222222' ||
        input === '3333333333' ||
        input === '4444444444' ||
        input === '5555555555' ||
        input === '6666666666' ||
        input === '7777777777' ||
        input === '8888888888' ||
        input === '9999999999'
    ) {
        return false;
    }
    const check = parseInt(input[9]);
    let sum = 0;
    let i;
    for (i = 0; i < 9; ++i) {
        sum += parseInt(input[i]) * (10 - i);
    }
    sum %= 11;
    return (sum < 2 && check === sum) || (sum >= 2 && check + sum === 11);
};

export const convertPersianNumbers = (input: string): string =>
    input.replace(/([۰-۹])/g, token =>
        String.fromCharCode(token.charCodeAt(0) - 1728)
    );

export const formatShortDuration = (duration: number): string => {
    const d = Math.floor(duration / 60);
    const minutes = d % 60;
    const hours = Math.floor(d / 60);

    return (
        (hours ? `${hours} ` + `س` + ` ` : '') +
        (minutes ? `${minutes} ` + `د` + ` ` : '')
    );
};

export const navigateToElement = (query: string) => {
    const element = document.querySelector(query) as HTMLDivElement;
    if (element) {
        window.scrollTo({
            left: 0,
            top: element.offsetTop,
            behavior: 'smooth',
        });
    }
};

export const navToSection = () => {
    const firstSection = document.querySelector(
        '[data-invalid-scroll="true"]'
    ) as HTMLDivElement | undefined;
    firstSection?.scrollIntoView({ behavior: 'smooth' });
    const focusInput = firstSection?.dataset?.focusEnabled;
    if (focusInput === 'true') {
        window.setTimeout(() => {
            const firstInput = document.querySelector(
                '[data-invalid-focus="true"]'
            ) as HTMLInputElement | undefined;
            firstInput?.focus();
        }, 500);
    }
};

export const convertToEnglishDigit = (value: string): string =>
    value.replace(
        /[\u0660-\u0669\u06F0-\u06F9]/g,
        c => `${c.charCodeAt(0) & 0xf}`
    );

export const normalizePersianCharacters = (value: string): string =>
    value.replaceAll('ي', 'ی').replaceAll('ك', 'ک');

export const formatSecondsToTimer = (seconds: number): string => {
    const h = Math.max(0, Math.floor(seconds / 3600));
    const m = Math.max(0, Math.floor(seconds / 60) % 60);
    const s = Math.max(0, seconds % 60);
    return `${h}:${m}:${s}`
        .split(':')
        .map(s => s.padStart(2, '0'))
        .join(':');
};

export function copyTextToClipboard(text: string) {
    if (!window || !window.getSelection) {
        return;
    }
    const dummy = document.createElement('p');
    dummy.textContent = text;
    document.body.appendChild(dummy);

    const range = document.createRange();
    range.setStartBefore(dummy);
    range.setEndAfter(dummy);

    const selection = window.getSelection();
    // First clear, in case the user already selected some other text
    selection?.removeAllRanges();
    selection?.addRange(range);

    document.execCommand('copy');
    document.body.removeChild(dummy);
}

export function interopDefault(promise: Promise<{ default?: any }>) {
    return promise.then(m => m.default || m);
}

/**
 * Generates the unique id for authenticated users
 */
export const generateUUID = () => {
    let d = new Date().getTime();
    if (
        typeof performance !== 'undefined' &&
        typeof performance.now === 'function'
    ) {
        d += performance.now();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (d + Math.random() * 16) % 16 | 0;
        d = Math.floor(d / 16);
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
};

export function formatDateRange(
    from: string | Date,
    to: string | Date,
    includeCurrentYear = false
) {
    const fromJs = dayConfig.js(from).startOf('day');
    const toJs = dayConfig.js(to).startOf('day');
    if (!fromJs.isSame(toJs, 'year')) {
        return `${fromJs.format('D MMMM YYYY')} تا ${toJs.format(
            'D MMMM YYYY'
        )}`;
    }
    let tempResult = '';
    if (!fromJs.isSame(dayConfig.js(), 'year') || includeCurrentYear) {
        tempResult += toJs.format(' YYYY');
    }
    if (!fromJs.isSame(toJs, 'month')) {
        return `${fromJs.format('D MMMM')} تا ${toJs.format(
            'D MMMM'
        )}${tempResult}`;
    }
    return `${fromJs.format('D')} تا ${toJs.format('D MMMM')}${tempResult}`;
}

export function formatDateList(...dates: (string | Date)[]) {
    const datesJs = dates
        .map(date => dayConfig.js(date).startOf('day'))
        .sort((a, b) => a.unix() - b.unix());
    const yearMap = new Map<string, Map<string, string[]>>();
    for (const date of datesJs) {
        const year = date.format('YYYY');
        const yearMonth = date.format('MMMM');
        if (yearMap.has(year)) {
            const yearItem = yearMap.get(year)!;
            if (yearItem.has(yearMonth)) {
                yearItem.get(yearMonth)?.push(date.format('D'));
            } else {
                yearItem.set(yearMonth, [date.format('D')]);
            }
        } else {
            const newMap = new Map<string, string[]>();
            newMap.set(yearMonth, [date.format('D')]);
            yearMap.set(year, newMap);
        }
    }
    const showYear =
        yearMap.size > 1 || !yearMap.has(dayConfig.js().format('YYYY'));
    const result = [];
    for (const [year, monthMap] of yearMap) {
        const currentMonthResult = [];
        let resultString = '';
        for (const [month, days] of monthMap) {
            let result = '';
            result += days.join(' و ');
            result += ` ${month}`;
            currentMonthResult.push(result);
        }
        resultString += currentMonthResult.join(' و ');
        if (showYear) {
            resultString += ` ${year}`;
        }
        result.push(resultString);
    }
    return result.join(' و ');
}

export const searchPersianInStrings = (
    searchKeyword: string,
    options: string[],
    searchInMiddle = false
) => {
    const splitAll = (text: string) => [...text.split(' '), text];
    const splitList = options.map(o =>
        splitAll(o.replace(/آ/g, 'ا').replace(/[)(]/g, ''))
    );
    const search = searchKeyword.replace(/آ/g, 'ا');
    const map: Record<string, string> = {
        Q: 'ض',
        W: 'ص',
        E: 'ث',
        R: 'ق',
        T: 'ف',
        Y: 'غ',
        U: 'ع',
        I: 'ه',
        O: 'خ',
        P: 'ح',
        '[': 'ج',
        ']': 'چ',
        A: 'ش',
        S: 'س',
        D: 'ی',
        F: 'ب',
        G: 'ل',
        H: 'ا',
        J: 'ت',
        K: 'ن',
        L: 'م',
        ';': 'ک',
        "'": 'گ',
        Z: 'ظ',
        X: 'ط',
        C: 'ز',
        V: 'ر',
        B: 'ذ',
        N: 'د',
        M: 'پ',
        ',': 'و',
        '\\': 'پ',
        ي: 'ی',
        ك: 'ک',
    };
    const derivedTerms = [
        search,
        search
            .toUpperCase()
            .replace(/\S/g, (token: string) => map[token] || token),
    ];
    let matched = false;
    let matchedPosition = '';

    derivedTerms.forEach(d => {
        if (!matched) {
            splitList.forEach((s, sIndex) => {
                if (!matched) {
                    s.forEach((l, lIndex) => {
                        if (!matched) {
                            const ma = l
                                .toUpperCase()
                                .startsWith(d.toUpperCase());
                            let maInMiddle = false;
                            if (searchInMiddle && !ma) {
                                maInMiddle = l
                                    .toUpperCase()
                                    .includes(d.toUpperCase());
                            }
                            matched = ma || maInMiddle;
                            if (matched) {
                                matchedPosition = `${sIndex}-${maInMiddle ? 2 : 1}-${lIndex}-${maInMiddle ? 2 : 1}`;
                            }
                        }
                    });
                }
            });
        }
    });

    return {
        matched,
        matchedPosition,
    };
};

const persianUnits = [
    'صفر',
    'یک',
    'دو',
    'سه',
    'چهار',
    'پنج',
    'شش',
    'هفت',
    'هشت',
    'نه',
];
const persianTens = [
    '',
    'ده',
    'بیست',
    'سی',
    'چهل',
    'پنجاه',
    'شصت',
    'هفتاد',
    'هشتاد',
    'نود',
];
const persianTeens = [
    'ده',
    'یازده',
    'دوازده',
    'سیزده',
    'چهارده',
    'پانزده',
    'شانزده',
    'هفده',
    'هجده',
    'نوزده',
];
const persianHundreds = [
    '',
    'صد',
    'دویست',
    'سیصد',
    'چهارصد',
    'پانصد',
    'ششصد',
    'هفتصد',
    'هشتصد',
    'نهصد',
];
const persianThousands = ['', 'هزار', 'میلیون', 'میلیارد'];

export function numberToPersianText(num: number): string {
    if (num === 0) {
        return persianUnits[0];
    }
    if (num < 0) {
        return `منفی ${numberToPersianText(-num)}`;
    }

    const parts: string[] = [];
    const numStr = num.toString();
    const groups: number[] = [];

    // Split number into groups of 3 digits
    for (let i = numStr.length; i > 0; i -= 3) {
        const start = Math.max(i - 3, 0);
        groups.unshift(parseInt(numStr.slice(start, i), 10));
    }

    groups.forEach((group, index) => {
        if (group === 0) {
            return;
        }

        const hundreds = Math.floor(group / 100);
        const tensUnits = group % 100;
        const tens = Math.floor(tensUnits / 10);
        const units = tensUnits % 10;

        const groupParts: string[] = [];

        if (hundreds) {
            groupParts.push(persianHundreds[hundreds]);
        }

        if (tensUnits >= 10 && tensUnits < 20) {
            groupParts.push(persianTeens[tensUnits - 10]);
        } else {
            if (tens) {
                groupParts.push(persianTens[tens]);
            }
            if (units) {
                groupParts.push(persianUnits[units]);
            }
        }

        const groupText = groupParts.join(' و ');
        const scale = persianThousands[groups.length - index - 1];
        parts.push(groupText + (scale ? ` ${scale}` : ''));
    });

    return parts.join(' و ');
}

export function numberToPersianCurrencyText(
    num: number,
    currency: 'ریال' | 'تومان' | '' = ''
): string {
    const integerPart = Math.floor(Math.abs(num));
    const decimalPart = Math.round((Math.abs(num) - integerPart) * 100); // up to 2 digits

    let result = numberToPersianText(integerPart);

    if (decimalPart > 0) {
        const decimalText = numberToPersianText(decimalPart);
        result += ` و ${decimalText} صدم`;
    }

    if (currency) {
        result += ` ${currency}ء`;
    }

    if (num < 0) {
        result = `منفی ${result}`;
    }

    return result;
}

export * from './Storage';
